import dayjs from 'dayjs'
import { defineStore } from 'pinia'

import type { JobType } from '~/gql/jobs/queries/findJob/types'
import type { JobAttributes, Location } from '~/gql/jobs/schema.generated'

// TODO: Revisit typechecking
const allowedJobTypeProperties = [
  'offerType',
  'features',
  'title',
  'applicationUrl',
  'applicationEmail',
  'companyName',
  'locationOptions',
  'startAt',
  'jobTypes',
  'description',
  'applicationInstructions',
  'country',
  'city',
  'zipcode',
  'street',
  'salaryFrom',
  'salaryTo',
] as const

type AllowedPropertiesUnionType = (typeof allowedJobTypeProperties)[number]

const ownJobOfferProperties = ['categoryIds', 'companyLogo', 'experience', 'locationIds', 'promoCodeId'] as const

type OwnJobOfferPropertiesUnionType = (typeof ownJobOfferProperties)[number]

type OwnJobOfferProperties = Pick<JobAttributes, OwnJobOfferPropertiesUnionType>

type JobOfferType = Pick<JobType, AllowedPropertiesUnionType> & OwnJobOfferProperties
type JobOfferPrice = {
  price: number
  currency: string
  currencySymbol: string
} | null
type JobOfferLocation = Partial<Location>

function isJobOfferTypeKey(key: string): key is keyof JobOfferType {
  return (
    allowedJobTypeProperties.includes(key as AllowedPropertiesUnionType) ||
    ownJobOfferProperties.includes(key as OwnJobOfferPropertiesUnionType)
  )
}

const freshJobOfferObject = function (): JobAttributes {
  return {
    startAt: dayjs(new Date()).format('YYYY-MM-DD'),
    locationIds: [],
    applicationUrl: null,
    applicationEmail: null,
    country: '',
    city: '',
    zipcode: '',
    street: '',
    offerType: '',
    features: [],
    title: '',
    companyName: '',
    locationOptions: [],
    jobTypes: [],
    description: '',
    applicationInstructions: '',
    categoryIds: [],
    experience: [],
  }
}

export const useNewJobStore = defineStore('newJob', {
  state: () => ({
    jobOfferDirty: false,
    jobOffer: freshJobOfferObject(),
    jobOfferPrice: null as JobOfferPrice,
    promoCodeDiscount: 0,
    promoCodeId: null,
    promoCode: '',
    locations: [{}] as JobOfferLocation[],
    companyLogoThumb: null as string | null,
    publishedAt: null as string | null,
  }),
  actions: {
    resetDirtyFlag() {
      this.jobOfferDirty = false
    },
    updateJobOffer(updates: Partial<JobOfferType>) {
      const jobOffer = this.jobOffer as Record<keyof JobOfferType, unknown>

      for (const key of Object.keys(updates)) {
        if (!isJobOfferTypeKey(key)) continue

        jobOffer[key] = updates[key]
      }

      this.jobOfferDirty = true
    },
    resetJobOffer() {
      this.jobOffer = freshJobOfferObject()
      this.jobOfferPrice = null
    },
    updateJobOfferPrice(price?: JobOfferPrice) {
      this.jobOfferPrice = price ?? null
    },
    removePromoCodeId() {
      this.promoCodeId = null
    },
    updatePromoCode(promoCode: string) {
      this.promoCode = promoCode
    },
    updatePromoCodeDiscount(promoCodeDiscount: number) {
      this.promoCodeDiscount = promoCodeDiscount
    },
    updateLocations(locations: JobOfferLocation[]) {
      this.locations = locations

      const locationIds = locations.map((location) => location.id).filter((locationId): locationId is string => Boolean(locationId))

      this.jobOffer.locationIds = locationIds
    },
    updateCompanyLogoThumb(newValue: string) {
      this.companyLogoThumb = newValue
    },
    updatePublishedAt(newValue: string) {
      this.publishedAt = newValue
    },
  },
})
